import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle2, AlertCircle, Info, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * T2370: neobvezno dejanje v obvestilu (vzorec Gmail — »Razveljavi«). Klik
 * izvede dejanje in obvestilo zapre.
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'loading';
  title: string;
  message?: string;
  action?: ToastAction;
}

interface ToastContextValue {
  /** Vrne id obvestila (T2370: klicatelj ga sme pozneje zapreti z `dismiss`). */
  toast: (t: Omit<Toast, 'id'>) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue>(null!);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  // T2072: `t` v zanki spodaj je obvestilo, zato prevajalnik kot `tr`.
  const { t: tr } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  // T2590: časovniki samodejnega zapiranja po id-ju obvestila. Brez tega je
  // časovnik preživel provider — v vitestu se je sprožil po razgradnji jsdom
  // (`window is not defined`, izhodna koda 1 ob samih zelenih testih), v
  // brskalniku pa kliče `setState` nad odklopljenim drevesom.
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((handle) => clearTimeout(handle));
      timers.clear();
    };
  }, []);

  const dismiss = useCallback((id: string) => {
    const handle = timersRef.current.get(id);
    if (handle !== undefined) {
      clearTimeout(handle);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(({ type, title, message, action }: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, type, title, message, action }]);
    
    if (type !== 'loading') {
      timersRef.current.set(
        id,
        setTimeout(() => {
          timersRef.current.delete(id);
          dismiss(id);
        }, 5000),
      );
    }
    
    return id;
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      {/* T1721: širino omeji NASPROTNI odmik, ne `w-full`. `w-full` je dal
          širino celotnega zaslona, `left-6` pa jo je odmaknil za 24 px, zato je
          desni rob pri 375 px padel na 399 — izmerjeno pri ročni verifikaciji
          V0097. Ker `ToastProvider` stoji nad celo aplikacijo, je bilo s tem
          odrezano vsako obvestilo na telefonu, skupaj z gumbom za zapiranje in
          morebitnim dejanjem (»Razveljavi«). `max-w-md` ostane: brez njega bi
          obojestranski odmik obvestilo na namizju raztegnil čez celo okno. */}
      <div className="fixed bottom-6 left-6 right-6 z-[100] flex flex-col gap-3 max-w-md">
        {toasts.map((t) => (
          <div
            key={t.id}
            // T2859: brez vloge bralnik zaslona obvestila (npr. »Razveljavi«) ne izreče.
            role={t.type === 'error' ? 'alert' : 'status'}
            className={cn(
              "p-4 rounded-xl border shadow-xl flex items-start gap-3 animate-in slide-in-from-left duration-300",
              t.type === 'success' ? "bg-[var(--bp-surface)] border-green-100 text-green-800" :
              t.type === 'error' ? "bg-[var(--bp-surface)] border-red-100 text-red-800" :
              t.type === 'loading' ? "bg-[var(--bp-surface)] border-[var(--bp-accent)]/20 text-[var(--bp-accent-text)]" :
              "bg-[var(--bp-surface)] border-[var(--bp-border-soft)] text-[var(--bp-text-2)]"
            )}
          >
            <div className="shrink-0 mt-0.5">
              {t.type === 'success' && <CheckCircle2 className="h-5 w-5 text-green-500" />}
              {t.type === 'error' && <AlertCircle className="h-5 w-5 text-red-500" />}
              {t.type === 'info' && <Info className="h-5 w-5 text-[var(--bp-accent-text)]" />}
              {t.type === 'loading' && <Loader2 className="h-5 w-5 text-[var(--bp-accent-text)] animate-spin" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-body font-bold leading-none mb-1">{t.title}</p>
              {t.message && <p className="text-dense opacity-80 leading-relaxed">{t.message}</p>}
            </div>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action?.onClick();
                  dismiss(t.id);
                }}
                className="shrink-0 rounded-md px-2 py-1 text-body font-semibold text-[var(--bp-accent-text)] hover:bg-[var(--bp-surface-2)] transition-colors"
              >
                {t.action.label}
              </button>
            )}
            <button onClick={() => dismiss(t.id)} aria-label={tr('common.close')} className="shrink-0 p-1 hover:bg-[var(--bp-surface-2)] rounded-md transition-colors">
               <X className="h-4 w-4 text-[var(--bp-mute)]" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
